import { useState, useEffect } from 'react';
import { observabilityService } from '../../services/observability.service';

interface Edge {
  id: string;
  name: string;
}

interface EdgeSelectorProps {
  selectedEdgeId: string;
  onEdgeChange: (edgeId: string) => void;
  className?: string;
}

/**
 * Shared edge selector dropdown with online/offline status indicator.
 * Used across monitoring pages for consistent edge selection.
 * Fetches edges from observabilityService.getRegisteredEdges() and
 * checks online status via edge_mqtt_connected metric.
 */
const EdgeSelector = ({ selectedEdgeId, onEdgeChange, className }: EdgeSelectorProps) => {
  const [edges, setEdges] = useState<Edge[]>([]);
  const [onlineEdges, setOnlineEdges] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchEdges = async () => {
      try {
        const res = await observabilityService.getRegisteredEdges();
        if (res.status === 'success' && res.data && res.data.length > 0) {
          const sorted = res.data.sort((a, b) => a.name.localeCompare(b.name));
          setEdges(sorted);

          if (!selectedEdgeId && sorted.length > 0) {
            onEdgeChange(sorted[0].id);
          }
        } else {
          const promRes = await observabilityService.getLabelValues('edge_id');
          if (promRes.status === 'success' && promRes.data && promRes.data.length > 0) {
            const fallback = promRes.data.map((id: string) => ({ id, name: id }));
            setEdges(fallback);
            if (!selectedEdgeId) onEdgeChange(promRes.data[0]);
          }
        }
      } catch (error) {
        console.error('Failed to fetch edges:', error);
      }
    };
    fetchEdges();
  }, []);

  // Check online status via telemetry
  useEffect(() => {
    const fetchOnlineStatus = async (isBackground = false) => {
      try {
        const options = isBackground ? { showLoading: false } : undefined;
        const res = await observabilityService.getEdgesTelemetry(options);
        if (res.status === 'success' && res.data) {
          const online = new Set<string>();
          for (const [edgeId, stats] of Object.entries(res.data)) {
            if (stats && (stats as any).cpu !== undefined) {
              online.add(edgeId);
              online.add(edgeId.toLowerCase());
            }
          }
          setOnlineEdges(online);
        }
      } catch {
        // Telemetry unavailable — don't block
      }
    };
    fetchOnlineStatus(false);
    const interval = setInterval(() => fetchOnlineStatus(true), 30000);
    return () => clearInterval(interval);
  }, []);

  const isOnline = selectedEdgeId
    ? onlineEdges.has(selectedEdgeId) || onlineEdges.has(selectedEdgeId.toLowerCase())
    : false;

  return (
    <div className={`flex items-center space-x-3 ${className || ''}`}>
      <select
        value={selectedEdgeId}
        onChange={(e) => onEdgeChange(e.target.value)}
        className="block w-64 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
      >
        {edges.length === 0 && <option value="">No edges found</option>}
        {edges.map(edge => (
          <option key={edge.id} value={edge.id}>
            {edge.name}
          </option>
        ))}
      </select>
      {selectedEdgeId && (
        <span className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
          isOnline
            ? 'bg-green-100 text-green-700'
            : 'bg-gray-100 text-gray-600'
        }`}>
          <span className={`h-2 w-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
          <span>{isOnline ? 'Online' : 'Offline'}</span>
        </span>
      )}
    </div>
  );
};

export default EdgeSelector;
